/**
 * Nearby retained sale or rental adverts around one property
 * (`property-market-offerings-v1`), with the saved assessment contributors
 * marked. Filters explore the cohort; they never change the assessment.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from './http'
import { useDealModelMode, useEntitlements } from './queries'
import { contract } from './diligence'

export type MarketTransactionType = 'sale' | 'rent'

export interface MarketOfferingFilters { bedrooms: number | null; propertyType: string | null; tenure: string | null; radiusMiles: number }

export interface MarketOfferingTypeAdjustment {
  ratio: [string, string]
  sourceType: string
  targetType: string
  basis: 'sale_asking'
}

export interface MarketOffering {
  id: string
  listingId: string
  address: string | null
  postcode: string | null
  pricePence: number | null
  propertyType: string | null
  bedrooms: number | null
  bathrooms: number | null
  tenure: string | null
  latitude: number | null
  longitude: number | null
  rightmoveUrl: string | null
  status: string | null
  distanceMiles: number | null
  areaSqm: number | null
  usedInAssessment: boolean
  assessmentUses: { price: boolean; unitPrice: boolean }
  assessmentPricePence: number | null
  observationBasis: 'current_retained_advert' | 'frozen_assessment_advert'
  /** Frozen assessment evidence; the current/raw advert price stays primary. */
  gdvTypeAdjustment?: MarketOfferingTypeAdjustment
  coefficientReleaseId?: string
  coefficientReleaseHash?: string
  assessmentAdjustedPricePence?: number | null
}

export interface MarketOfferings {
  contractVersion: 'property-market-offerings-v1'
  subject: { propertyId: string; address: string | null; postcode: string | null; latitude: number | null; longitude: number | null }
  defaults: MarketOfferingFilters
  filters: MarketOfferingFilters & { transactionType: MarketTransactionType }
  radiusUsedMiles: number
  totalCount: number
  currentMatchingCount: number
  assessmentCount: number
  assessmentReturnedCount: number
  returnedCount: number
  truncated: boolean
  rows: MarketOffering[]
  warnings: string[]
}

const c = contract('market_offerings_contract_mismatch')

function text(value: unknown, label: string, nullable = true): void {
  if ((nullable && value === null) || typeof value === 'string') return
  c.fail(`${label} must be ${nullable ? 'null or ' : ''}text`)
}

function numeric(value: unknown, label: string, nullable = true, integer = false, minimum = 0, maximum = Number.MAX_SAFE_INTEGER): void {
  if (nullable && value === null) return
  if (typeof value !== 'number' || !Number.isFinite(value) || (integer && !Number.isSafeInteger(value)) || value < minimum || value > maximum) c.fail(`${label} has an invalid number`)
}

export function hasMapPosition(value: { latitude: number | null; longitude: number | null }): boolean {
  return value.latitude !== null && value.longitude !== null && Number.isFinite(value.latitude) && Number.isFinite(value.longitude)
    && Math.abs(value.latitude) <= 90 && Math.abs(value.longitude) <= 180
}

/** Provider links are retained evidence. Do not construct a URL from an unverified ID. */
export function safeRightmoveUrl(value: string | null): string | null {
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && !url.username && !url.password && !url.port
      && (url.hostname === 'www.rightmove.co.uk' || url.hostname === 'rightmove.co.uk')
      && /^\/properties\/\d+(?:\/)?$/.test(url.pathname) ? url.href : null
  } catch { return null }
}

export function parseMarketOfferings(value: unknown, propertyId: string, transactionType: MarketTransactionType): MarketOfferings {
  const data = c.record(c.record(value, 'market offerings envelope').data, 'market offerings')
  if (data.contractVersion !== 'property-market-offerings-v1') c.fail('Market offerings version is unsupported')
  const subject = c.record(data.subject, 'subject')
  if (subject.propertyId !== propertyId) c.fail('Market offerings belong to a different property')
  for (const key of ['address', 'postcode']) text(subject[key], `subject.${key}`)
  for (const [key, max] of [['latitude', 90], ['longitude', 180]] as const) numeric(subject[key], `subject.${key}`, true, false, -max, max)
  for (const name of ['defaults', 'filters']) {
    const filters = c.record(data[name], name)
    numeric(filters.bedrooms, `${name}.bedrooms`, true, true, 0, 100)
    text(filters.propertyType, `${name}.propertyType`); text(filters.tenure, `${name}.tenure`)
    numeric(filters.radiusMiles, `${name}.radiusMiles`, false, false, 0, 5)
    if (name === 'filters' && filters.transactionType !== transactionType) c.fail('Market offerings transaction type is inconsistent')
  }
  numeric(data.radiusUsedMiles, 'radiusUsedMiles', false, false, 0, 5)
  for (const key of ['totalCount', 'returnedCount', 'currentMatchingCount', 'assessmentCount', 'assessmentReturnedCount']) numeric(data[key], key, false, true)
  if (typeof data.truncated !== 'boolean' || !Array.isArray(data.rows) || data.rows.length !== data.returnedCount || data.rows.length > 200
    || (data.totalCount as number) < data.rows.length || data.truncated !== ((data.totalCount as number) > data.rows.length)) c.fail('Market offerings counts are inconsistent')
  const ids = new Set<string>()
  let assessmentRows = 0
  for (const [index, entry] of (data.rows as unknown[]).entries()) {
    const row = c.record(entry, `rows[${index}]`)
    for (const key of ['id', 'listingId']) text(row[key], key, false)
    if (ids.has(String(row.id))) c.fail('Market offering identity is duplicated')
    ids.add(String(row.id))
    for (const key of ['address', 'postcode', 'propertyType', 'tenure', 'rightmoveUrl', 'status']) text(row[key], key)
    for (const key of ['pricePence', 'bedrooms', 'bathrooms', 'assessmentPricePence']) numeric(row[key], key, true, true)
    if (row.observationBasis !== 'current_retained_advert' && row.observationBasis !== 'frozen_assessment_advert') c.fail('Market offering observation basis is unsupported')
    const uses = c.record(row.assessmentUses, 'assessmentUses')
    if (typeof row.usedInAssessment !== 'boolean' || typeof uses.price !== 'boolean' || typeof uses.unitPrice !== 'boolean' || row.usedInAssessment !== (uses.price || uses.unitPrice)) c.fail('Assessment membership is inconsistent')
    if (row.usedInAssessment) {
      if (row.assessmentPricePence === null || (row.assessmentPricePence as number) <= 0) c.fail('Assessment contributor has no retained positive price')
      assessmentRows += 1
    }
    if (row.gdvTypeAdjustment !== undefined && row.gdvTypeAdjustment !== null) {
      const adjustment = c.record(row.gdvTypeAdjustment, 'gdvTypeAdjustment')
      if (transactionType !== 'sale' || row.usedInAssessment !== true || adjustment.basis !== 'sale_asking'
        || !Array.isArray(adjustment.ratio) || adjustment.ratio.length !== 2
        || !adjustment.ratio.every(value => typeof value === 'string' && /^[1-9]\d*$/.test(value))) c.fail('GDV type adjustment is invalid')
      for (const key of ['sourceType', 'targetType']) {
        text(adjustment[key], `gdvTypeAdjustment.${key}`, false)
        if (!(adjustment[key] as string).trim()) c.fail('GDV type adjustment category is missing')
      }
      text(row.coefficientReleaseId, 'coefficientReleaseId', false)
      text(row.coefficientReleaseHash, 'coefficientReleaseHash', false)
      if (!(row.coefficientReleaseId as string).trim() || !/^[0-9a-f]{64}$/.test(row.coefficientReleaseHash as string)) c.fail('GDV coefficient release identity is invalid')
      numeric(row.assessmentAdjustedPricePence, 'assessmentAdjustedPricePence', true, true)
    }
    for (const key of ['distanceMiles', 'areaSqm']) numeric(row[key], key)
    for (const [key, max] of [['latitude', 90], ['longitude', 180]] as const) numeric(row[key], key, true, false, -max, max)
  }
  if (assessmentRows !== data.assessmentReturnedCount || assessmentRows > (data.assessmentCount as number)) c.fail('Assessment contributor counts are inconsistent')
  if (data.statistics !== null && data.statistics !== undefined) c.record(data.statistics, 'statistics')
  c.strings(data.warnings, 'warnings')
  return data as unknown as MarketOfferings
}

/**
 * Query parameters: the transaction type, plus every filter once the user changes one.
 * An empty filter value is meaningful here ("all bedrooms"), while an absent one means
 * the subject's default. `resolve()` skips '' values, so an explicit empty value is
 * passed boxed: it is not identical to '' and still serialises as `key=`.
 */
function explicit(value: string): string {
  // eslint-disable-next-line no-new-wrappers
  return value === '' ? (new String('') as unknown as string) : value
}

function offeringsQuery(transactionType: MarketTransactionType, overrides: MarketOfferingFilters | null): Record<string, string> {
  const query: Record<string, string> = { transactionType }
  if (overrides) {
    query.bedrooms = explicit(overrides.bedrooms === null ? '' : String(overrides.bedrooms))
    query.propertyType = explicit(overrides.propertyType ?? '')
    query.tenure = explicit(overrides.tenure ?? '')
    query.radiusMiles = String(overrides.radiusMiles)
  }
  return query
}

export function useMarketOfferings(propertyId: string, transactionType: MarketTransactionType, overrides: MarketOfferingFilters | null, enabled = true) {
  const mode = useDealModelMode()
  const entitlements = useEntitlements()
  const query = offeringsQuery(transactionType, overrides)
  const search = Object.entries(query).map(([key, value]) => `${key}=${String(value)}`).join('&')
  const scope = { capabilities: entitlements.data?.capabilities, access: entitlements.data?.access, trialLimited: entitlements.data?.trialLimited }
  return useQuery({
    queryKey: ['marketOfferings', propertyId, search, mode.data?.activeMode ?? null, mode.data?.preferenceRevision ?? null, scope],
    queryFn: async ({ signal }) => parseMarketOfferings(await api('marketOfferings', { params: { propertyId }, query, signal }), propertyId, transactionType),
    enabled: enabled && propertyId.length > 0 && mode.isSuccess && entitlements.isSuccess && mode.data.activeMode !== null,
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
